import { useToolSystem } from '@/context/ToolSystemContext';
import { useRuleSets } from '@/hooks/useRuleSets';
import type { ToolSystem } from '@/types/toolSystem.types';
import { useCallback, useEffect, useState } from 'react';

/** One remembered choice per tool system: the two have entirely separate rule sets. */
function storageKey(toolSystem: ToolSystem): string {
  return `eda_new_project_rule_set_${toolSystem}`;
}

/** Read the remembered choice; a browser that refuses storage simply has none. */
function readStored(toolSystem: ToolSystem): string | null {
  try {
    return localStorage.getItem(storageKey(toolSystem));
  } catch (error) {
    console.warn('Could not read the remembered rule set:', error);
    return null;
  }
}

function writeStored(toolSystem: ToolSystem, name: string): void {
  try {
    localStorage.setItem(storageKey(toolSystem), name);
  } catch (error) {
    console.warn('Could not remember the rule set:', error);
  }
}

/**
 * The rule set the next project will be created with, within the active tool system.
 *
 * A project's rule set is fixed once its model has been processed, so the choice has to be
 * made before the file is dropped. It sticks across reloads because uploads come in batches
 * and re-picking per file would be noise — per system, so switching systems and back does
 * not lose the choice made in either.
 */
export const useNewProjectRuleSet = () => {
  const { toolSystem } = useToolSystem();
  const { ruleSets, isLoading, error } = useRuleSets();
  const [name, setName] = useState<string | null>(() => readStored(toolSystem));

  // Switching systems reopens that system's own remembered choice.
  useEffect(() => {
    setName(readStored(toolSystem));
  }, [toolSystem]);

  // Fall back to the backend's default whenever the remembered name is gone or was never set.
  useEffect(() => {
    if (ruleSets.length === 0) return;
    const known = ruleSets.some((ruleSet) => ruleSet.name === name);
    if (known) return;
    const fallback = ruleSets.find((ruleSet) => ruleSet.is_default) ?? ruleSets[0];
    setName(fallback.name);
  }, [ruleSets, name]);

  const choose = useCallback(
    (next: string) => {
      setName(next);
      writeStored(toolSystem, next);
    },
    [toolSystem]
  );

  return { ruleSets, name, choose, isLoading, error };
};
